// src/features/auth/RetailOneLogin.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { Eye, EyeOff } from 'lucide-react';
import logo from '../../SBA-Logo.png';
import backgroundImage from '../../img/back.jpg';
import './RetailOneLogin.css'; // We'll create this CSS file

export default function RetailOneLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { loginRetailOne } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await loginRetailOne(email, password);
    
    if (!result.success) {
      setError(result.message || 'Invalid credentials');
      setLoading(false);
      return;
    }

    // Check if 2FA is required
    if (result.requires_2fa) {
      navigate('/verify-otp', { 
        state: { 
          email: result.email, 
          session_id: result.session_id 
        } 
      });
    } else {
      navigate('/retailone-dashboard');
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-wrapper">
        
        {/* Left Side - Welcome Section */}
        <div className="welcome-section">
          <div>
            <div className="logo-container">
              <div className="logo-box">
                <img 
                  src={logo} 
                  alt="SBA Analytics Logo" 
                  className="logo-image"
                />
              </div>
              <span className="logo-text">
                SBA Systems
              </span>
            </div>

            <div className="welcome-content">
              <h1 className="welcome-title">
                Welcome to SBA Analytics
              </h1>
              <p className="welcome-subtitle">
                Sign in to access your SBA analytics dashboard. 
                Track members, tee times, and club performance.
              </p>
            </div>

            <div className="info-box">
              {/* Optional: Add golf club info or stats here */}
            </div>
          </div>

          {/* Footer */}
          <div className="footer-text">
            © {new Date().getFullYear()} SBA Analytics. All rights reserved.
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-section">
          {error && (
            <Alert variant="danger" className="error-alert">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <div className="form-group">
              <Form.Label className="form-label">
                Email address
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="form-input"
                onFocus={(e) => {
                  e.target.style.borderColor = '#0ea5e9';
                  e.target.style.boxShadow = '0 0 0 2px rgba(14, 165, 233, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1DCEB';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div className="form-group password-group">
              <div className="password-header">
                <Form.Label className="form-label">
                  Password
                </Form.Label>
                <a href="/forgot-password" className="forgot-link">
                  Forgot password?
                </a>
              </div>
              <div className="password-input-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder=""
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="form-input password-input"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0ea5e9';
                    e.target.style.boxShadow = '0 0 0 2px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1DCEB';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="submit-button"
              onMouseEnter={(e) => {
                if (!loading) e.target.style.backgroundColor = '#0284c7';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#0ea5e9';
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>

            <div className="support-section">
              <p className="support-text">
                Need help?{' '}
                <a href="/support" className="support-link">
                  Contact support
                </a>
              </p>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}